import { useState, type ReactNode, type FormEvent } from 'react';

const STEALTH_PASSWORD = import.meta.env.VITE_STEALTH_PASSWORD || '';
const STORAGE_KEY = 'stealth_auth';
const API_URL = import.meta.env.VITE_API_URL || '';

export function StealthGate({ children }: { children: ReactNode }) {
  const [authenticated, setAuthenticated] = useState(
    () => !STEALTH_PASSWORD || sessionStorage.getItem(STORAGE_KEY) === 'true'
  );
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  // If no stealth password configured, gate is disabled — allow all visitors through
  if (authenticated) {
    return <>{children}</>;
  }

  function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault();
    if (password === STEALTH_PASSWORD) {
      sessionStorage.setItem(STORAGE_KEY, 'true');
      setAuthenticated(true);
    } else {
      setError(true);
      setPassword('');
    }
  }

  async function handleNewsletterSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      await fetch(`${API_URL}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'stealth_gate' }),
      });
      setSubscribed(true);
      setEmail('');
    } catch {
      // Silently succeed — non-critical
      setSubscribed(true);
    } finally {
      setSubscribing(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#0c0c0c] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Wordmark */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded border border-white/10 bg-white/[0.04] mb-5">
            <svg viewBox="0 0 32 32" className="w-8 h-8">
              <text
                x="16"
                y="23"
                fontFamily="Georgia, serif"
                fontSize="22"
                fontWeight="bold"
                fill="#f0ece4"
                textAnchor="middle"
              >
                J
              </text>
            </svg>
          </div>
          <h1
            className="text-2xl tracking-tight"
            style={{ color: '#f0ece4', fontFamily: 'Georgia, serif' }}
          >
            Jerico
          </h1>
          <p className="text-sm mt-2 tracking-wide" style={{ color: '#a8a29e' }}>
            Infrastructure for Fractional Counsel
          </p>
        </div>

        {/* Password form */}
        <form
          onSubmit={handlePasswordSubmit}
          className="rounded border border-white/[0.08] bg-white/[0.03] p-6 mb-3"
        >
          <label
            htmlFor="stealth-password"
            className="block text-xs font-medium tracking-widest uppercase mb-3"
            style={{ color: '#57534e' }}
          >
            Password
          </label>
          <input
            id="stealth-password"
            type="password"
            required
            autoFocus
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(false); }}
            className="w-full px-4 py-3 rounded bg-white/[0.04] border border-white/[0.08] text-sm focus:outline-none focus:border-white/20 transition-colors"
            style={{ color: '#f0ece4' }}
            placeholder="Enter access password"
          />
          {error && (
            <p className="mt-2 text-xs" style={{ color: '#f87171' }}>Incorrect password.</p>
          )}
          <button
            type="submit"
            className="mt-4 w-full px-4 py-3 rounded text-sm font-medium tracking-wide transition-opacity hover:opacity-90"
            style={{ backgroundColor: '#c9a96e', color: '#0c0c0c' }}
          >
            Enter
          </button>
        </form>

        {/* Early access form */}
        <div className="rounded border border-white/[0.08] bg-white/[0.03] p-6">
          {subscribed ? (
            <div className="text-center py-2">
              <div className="flex items-center justify-center gap-2 mb-1" style={{ color: '#34d399' }}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm font-medium">Request received.</span>
              </div>
              <p className="text-xs" style={{ color: '#57534e' }}>We'll be in touch.</p>
            </div>
          ) : (
            <form onSubmit={handleNewsletterSubmit}>
              <label
                htmlFor="newsletter-email"
                className="block text-xs font-medium tracking-widest uppercase mb-3"
                style={{ color: '#57534e' }}
              >
                Request Early Access
              </label>
              <div className="flex gap-2">
                <input
                  id="newsletter-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-4 py-3 rounded bg-white/[0.04] border border-white/[0.08] text-sm focus:outline-none focus:border-white/20 transition-colors"
                  style={{ color: '#f0ece4' }}
                  placeholder="your@firm.com"
                />
                <button
                  type="submit"
                  disabled={subscribing}
                  className="px-4 py-3 rounded text-sm font-medium tracking-wide transition-opacity hover:opacity-90 disabled:opacity-40 whitespace-nowrap"
                  style={{ backgroundColor: '#c9a96e', color: '#0c0c0c' }}
                >
                  {subscribing ? '...' : 'Request'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
